import { useEffect, useMemo, useRef, useState } from "react";

import { withTranslation } from "react-i18next";
import { orderBy, isEmpty } from "lodash";

import debug from "@plannel/utils/debug-util";
import BODService from "@plannel/services/data/bod-service";
import siteService from "@plannel/services/data/site-master-service";
import GroupedTokenSelect from "@plannel/components/ui/GroupedTokenSelect";

const NO_SITE = "__noSite__";
const ORIGIN = "originLocation";

/*
 * 출발지/도착지 로케이션 다중 선택 — 설정 모달(User Customization) 본문용.
 *
 * 예전에는 사이트(부모) 체크박스를 가진 2단 트리였다. 그런데 **저장되는 건 로케이션뿐**이다 —
 * `selectedLocation` 은 locCd 로 걸러낸 로케이션 객체 배열만 넘긴다. 사이트 노드는 값이 아니라
 * "그 밑을 한 번에 켜는 편의 장치"였고, 그걸 유지하느라 부모↔자식 체크 상태를 손으로 맞추는 코드가
 * 200줄 가까이 있었다(하위가 전부 켜지면 상위도 켜기 / 하나 풀리면 상위도 끄기 / RichTreeView
 * apiRef 로 강제 선택 …). 사이트를 **그룹 제목**으로 내리니 그 동기화가 통째로 필요 없어졌다.
 * 규격은 `toggle` 스킬 §1-2 / `ui/GroupedTokenSelect`.
 *
 * props
 *   open            : 모달 열림. 열릴 때마다 목록을 다시 읽는다
 *   filterValue     : 저장돼 있던 locCd 배열(값이 없으면 `[undefined]` 로 올 수 있어 걸러 쓴다)
 *   title           : "originLocation" | "destLocation" — 같은 lookup 에서 어느 쪽을 뽑을지
 *   selectedLocation: 선택이 바뀔 때 **로케이션 객체 배열**로 콜백(호출측 계약, 바꾸지 말 것)
 */
const MultiSelectLocation = ({ open, filterValue, title, selectedLocation }) => {
  const [locations, setLocations] = useState([]);
  const [sites, setSites] = useState([]);
  const [selected, setSelected] = useState([]);
  const initialized = useRef(false);

  const retrieveSiteData = () => {
    siteService.getAllForCSV()
      .then(response => {
        const { results } = response.data;
        if (!isEmpty(results)) {
          setSites(results);
        }
      })
      .catch(e => {
        debug(e);
      });
  };

  const retrieveOriginDestLocations = () => {
    BODService.getLookup()
      .then(response => {
        const { results } = response.data;
        const originMap = new Map();
        const destMap = new Map();

        for (const result of results) {
          if (result.originLocation && !originMap.has(result.originLocation.locCd)) {
            result.originLocation.site = result.originSite;
            originMap.set(result.originLocation.locCd, result.originLocation);
          }
          if (result.destLocation && !destMap.has(result.destLocation.locCd)) {
            result.destLocation.site = result.destSite;
            destMap.set(result.destLocation.locCd, result.destLocation);
          }
        }

        const picked = title === ORIGIN ? originMap : destMap;
        setLocations(orderBy(Array.from(picked.values()), ["name"], ["asc"]));
      })
      .catch(e => {
        debug(e);
      });
  };

  // 사이트별로 묶는다. 사이트를 못 찾은 로케이션은 제목 없는 그룹으로 맨 뒤에
  const groups = useMemo(() => {
    const siteNames = new Map(sites.map(site => [site.siteCd, site.name]));
    const bySite = new Map();
    const ungrouped = [];

    for (const location of locations) {
      const option = { value: location.locCd, label: location.name };
      const siteCd = location.site?.siteCd;
      if (siteCd && siteNames.has(siteCd)) {
        if (!bySite.has(siteCd)) {
          bySite.set(siteCd, { key: siteCd, label: siteNames.get(siteCd), options: [] });
        }
        bySite.get(siteCd).options.push(option);
      } else {
        ungrouped.push(option);
      }
    }

    const grouped = orderBy([...bySite.values()], ["label"], ["asc"]);
    return ungrouped.length > 0
      ? [...grouped, { key: NO_SITE, label: "", options: ungrouped }]
      : grouped;
  }, [locations, sites]);

  const handleChange = next => {
    setSelected(next);
    selectedLocation(locations.filter(location => next.includes(location.locCd)));
  };

  /*
   * 저장값 복원. `filterValue` 는 호출측에서 매 렌더 새 배열로 만들어 넘기므로 **의존성에 넣으면 안 된다**
   * (넣으면 루프가 된다). 목록이 준비된 뒤 열릴 때 한 번만 채우고, 닫히면 플래그를 되돌린다.
   */
  useEffect(() => {
    if (!open) {
      initialized.current = false;
      return;
    }
    if (!initialized.current && locations.length > 0) {
      initialized.current = true;
      setSelected((filterValue ?? []).filter(Boolean));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, locations]);

  useEffect(() => {
    retrieveOriginDestLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sites]);

  useEffect(() => {
    if (open) {
      retrieveSiteData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return <GroupedTokenSelect groups={groups} value={selected} onChange={handleChange} />;
};

export default withTranslation()(MultiSelectLocation);
